import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  PlayCircle, Volume2, VolumeX, RotateCcw, Check, Coins,
  Type, Mic, CalendarClock, Download, Sparkles, Music, Play,
} from "lucide-react";
import { FaYoutube, FaTiktok, FaInstagram } from "react-icons/fa";

const STEP_ICONS = { plan: Coins, topic: Type, narrator: Mic, music: Music, publish: CalendarClock };
const STEP_DURATION_MS = 4200; // fallback advance when narration is muted/unavailable

function StepVisual({ stepKey }) {
  if (stepKey === "plan") {
    const plans = [
      { name: "Starter", price: 19 },
      { name: "Creator", price: 39, hot: true },
      { name: "Studio", price: 79 },
    ];
    return (
      <div className="grid grid-cols-3 gap-2 w-full">
        {plans.map((p) => (
          <div key={p.name} className={`rounded-md border p-3 text-center ${p.hot ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0A0A0A]"}`}>
            {p.hot && <span className="text-[9px] uppercase tracking-wider text-blue-300">Popular</span>}
            <p className="font-head font-bold mt-1">{p.name}</p>
            <p className="font-head text-2xl font-black">${p.price}<span className="text-xs text-zinc-500">/mo</span></p>
          </div>
        ))}
      </div>
    );
  }
  if (stepKey === "topic") {
    return (
      <div className="w-full space-y-3">
        <div className="rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2.5 text-sm text-zinc-200 flex items-center gap-2">
          <Type className="h-4 w-4 text-blue-400" /> The science of building wealth quietly
        </div>
        <div className="flex flex-wrap gap-2">
          {["Finance", "Science", "History", "Motivation"].map((c, i) => (
            <span key={c} className={`text-xs px-3 py-1.5 rounded-full border ${i === 0 ? "border-blue-500 bg-blue-600/20 text-white" : "border-white/10 text-zinc-400"}`}>{c}</span>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-emerald-400"><Sparkles className="h-3.5 w-3.5" /> AI script generated</div>
      </div>
    );
  }
  if (stepKey === "narrator") {
    const voices = ["Sarah", "Roger", "Laura"];
    return (
      <div className="w-full space-y-2">
        {voices.map((v, i) => (
          <div key={v} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 ${i === 0 ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0A0A0A]"}`}>
            <Mic className={`h-4 w-4 ${i === 0 ? "text-blue-400" : "text-zinc-500"}`} />
            <span className="text-sm flex-1">{v}</span>
            <div className="flex items-end gap-0.5 h-4">
              {[6, 12, 8, 14, 7, 11].map((h, j) => (
                <span key={h} className={`w-0.5 rounded-full ${i === 0 ? "bg-blue-400" : "bg-zinc-700"}`} style={{ height: `${h}px` }} />
              ))}
            </div>
            {i === 0 && <Check className="h-4 w-4 text-blue-400" />}
          </div>
        ))}
      </div>
    );
  }
  if (stepKey === "music") {
    const tracks = [
      { name: "Epic Drive", mood: "Motivation" },
      { name: "Dark Suspense", mood: "True crime" },
      { name: "Calm Piano", mood: "Finance" },
    ];
    return (
      <div className="w-full space-y-2">
        {tracks.map((t, i) => (
          <div key={t.name} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 ${i === 0 ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0A0A0A]"}`}>
            <Music className={`h-4 w-4 ${i === 0 ? "text-blue-400" : "text-zinc-500"}`} />
            <span className="text-sm flex-1">{t.name} <span className="text-xs text-zinc-500">· {t.mood}</span></span>
            {i === 0 ? (
              <span className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-300 border border-blue-500/40 rounded-full px-2 py-0.5"><Play className="h-3 w-3" /> voice + music</span>
            ) : (
              <div className="flex items-end gap-0.5 h-4">
                {[6, 12, 8, 14, 7, 11].map((h) => (
                  <span key={h} className="w-0.5 rounded-full bg-zinc-700" style={{ height: `${h}px` }} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  // publish
  return (
    <div className="w-full space-y-3">
      <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm text-zinc-200"><CalendarClock className="h-4 w-4 text-blue-400" /> Mon, 9:00 AM</span>
        <div className="flex items-center gap-2 text-zinc-400">
          <FaYoutube className="h-4 w-4" /><FaTiktok className="h-4 w-4" /><FaInstagram className="h-4 w-4" />
        </div>
      </div>
      <div className="flex gap-2">
        <span className="flex-1 text-center text-sm bg-blue-600 rounded-md py-2 font-semibold">Schedule series</span>
        <span className="flex items-center gap-1.5 text-sm border border-white/15 rounded-md px-3 py-2 text-zinc-200"><Download className="h-4 w-4" /> Download</span>
      </div>
    </div>
  );
}

export function HowItWorksButton({ variant = "hero", className = "" }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [current, setCurrent] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const loadSteps = () => {
    if (steps.length) return;
    api.get("/howitworks").then((r) => setSteps(r.data.steps || [])).catch(() => {});
  };

  const openDialog = () => {
    loadSteps();
    setCurrent(0);
    setOpen(true);
  };

  const stopMedia = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  // Auto-advancing, looping playback synced to narration when available
  useEffect(() => {
    if (!open || steps.length === 0) return;
    stopMedia();
    const step = steps[current];
    const advance = () => setCurrent((c) => (c + 1) % steps.length);

    if (!muted && step.audio) {
      const audio = new Audio(step.audio);
      audio.onended = advance;
      audio.onerror = () => { timerRef.current = setTimeout(advance, STEP_DURATION_MS); };
      audioRef.current = audio;
      audio.play().catch(() => { timerRef.current = setTimeout(advance, STEP_DURATION_MS); });
    } else {
      timerRef.current = setTimeout(advance, STEP_DURATION_MS);
    }
    return stopMedia;
  }, [open, current, muted, steps]);

  const close = () => { stopMedia(); setOpen(false); };
  const replay = () => { stopMedia(); setCurrent(0); };

  const step = steps[current];
  const Icon = step ? STEP_ICONS[step.key] : PlayCircle;

  const triggerCls = variant === "hero"
    ? "inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-5 py-3 rounded-md text-sm font-semibold transition-colors"
    : "inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-medium transition-colors";

  return (
    <>
      <button onClick={openDialog} data-testid="how-it-works-btn" className={`${triggerCls} ${className}`}>
        <PlayCircle className="h-4 w-4 text-blue-400" /> How it works
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg p-0 overflow-hidden" data-testid="how-it-works-dialog">
          <DialogTitle className="sr-only">How Videoileo works</DialogTitle>
          <DialogDescription className="sr-only">A narrated four-step walkthrough of building a faceless video.</DialogDescription>

          {/* Stage */}
          <div className="relative bg-gradient-to-br from-blue-950/40 to-[#0A0A0A] p-6 min-h-[300px] flex flex-col">
            <div className="absolute inset-0 grid-noise opacity-60" />
            <div className="relative flex items-center justify-between mb-5">
              <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">How it works</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setMuted((m) => !m)} data-testid="hiw-mute-btn" title={muted ? "Unmute narration" : "Mute narration"} className="h-8 w-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center transition-colors">
                  {muted ? <VolumeX className="h-4 w-4 text-zinc-400" /> : <Volume2 className="h-4 w-4 text-blue-400" />}
                </button>
                <button onClick={replay} data-testid="hiw-replay-btn" title="Replay" className="h-8 w-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <RotateCcw className="h-4 w-4 text-zinc-400" />
                </button>
              </div>
            </div>

            {step ? (
              <div key={current} className="relative flex-1 flex flex-col animate-fade-up">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-md bg-blue-600 flex items-center justify-center font-head font-black">{step.n}</div>
                  <div>
                    <p className="font-head text-xl font-bold leading-tight flex items-center gap-2">{Icon && <Icon className="h-5 w-5 text-blue-400" />}{step.title}</p>
                  </div>
                </div>
                <div className="flex-1 flex items-center"><StepVisual stepKey={step.key} /></div>
                <p className="text-sm text-zinc-300 mt-4 leading-relaxed">{step.caption}</p>
              </div>
            ) : (
              <div className="relative flex-1 flex items-center justify-center text-zinc-500 text-sm">Loading walkthrough…</div>
            )}
          </div>

          {/* Progress + footer */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              {steps.map((s, i) => (
                <button
                  key={s.key}
                  onClick={() => setCurrent(i)}
                  data-testid={`hiw-dot-${i}`}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${i === current ? "bg-blue-500" : i < current ? "bg-blue-500/40" : "bg-white/10"}`}
                />
              ))}
            </div>
            <a href="/register" data-testid="hiw-cta" className="block text-center bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md text-sm font-semibold transition-colors">
              Start building free
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
